import {
  orangeShades,
  primaryColors,
  transparentColors,
  whiteShades
} from '@/assets/styles/helpers/colors.styles'
import { floatingSurface } from '@/shared/styles/floatingSurface.styles'

export const datePickerStyles = {
  '.MuiFormLabel-root': {
    color: `${whiteShades.shade1} !important`,
    fontFamily: 'helvetica-neue-lt-pro-cond, sans-serif',
    fontWeight: 900,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    transform: 'translate(14px, 7px) scale(1)',
    '&[data-shrink="true"]': {
      transform: 'translate(14px, -9px) scale(0.75)'
    },
    '&.Mui-focused': {
      color: `${primaryColors.white} !important`
    }
  },
  '.MuiOutlinedInput-root': {
    color: primaryColors.white,
    '&.MuiInputBase-sizeSmall': {
      paddingLeft: '0.25rem'
    }
  },
  '.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline': {
    borderColor: `${primaryColors.white} !important`
  },

  '& .MuiPickersOutlinedInput-notchedOutline': {
    borderColor: `${whiteShades.shade1} !important`,
    borderWidth: '2px !important',
    top: '-4px',
    'legend span': {
      fontFamily: 'helvetica-neue-lt-pro-cond, sans-serif',
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '0.65rem',

      paddingLeft: '0.25rem',
      paddingRight: '0.25rem',

      '&[aria-hidden="true"]': {
        paddingLeft: '0',
        paddingRight: '0'
      }
    }
  },
  '& .MuiPickersInputBase-root': {
    color: whiteShades.shade1,
    '&.Mui-focused': {
      color: primaryColors.white,
      '.MuiButtonBase-root': {
        color: primaryColors.white
      }
    }
  },
  '& .MuiButtonBase-root': {
    color: primaryColors.white,
    outline: 'none !important',
    padding: '0 0.5rem',
    svg: {
      fontSize: '1.25rem'
    },
    '&:hover': {
      color: whiteShades.shade1
    },
    '&:active': {
      color: whiteShades.shade2
    }
  },
  '& .MuiPickersSectionList-root': {
    padding: '3px 4px 3px 8px',
    fontFamily: 'helvetica-neue-lt-pro, sans-serif'
  },
  '& .MuiPickersSectionList-section': {
    lineHeight: '1.125rem'
  },
  '& .MuiPickersSectionList-sectionContent, .MuiPickersInputBase-sectionAfter':
    {
      fontFamily: 'helvetica-neue-lt-pro, sans-serif',
      fontSize: '1rem',
      lineHeight: '1.125rem',
      fontWeight: 400
    },
  '& .MuiPickersOutlinedInput-root': {
    padding: '5px 14px 5px 4px'
  }
}

export const datePickerPopperStyles = {
  '& .MuiPaper-root': {
    ...floatingSurface
  },
  '& .MuiButtonBase-root': {
    color: primaryColors.white,
    outline: 'none !important'
  },
  '& .MuiPickersCalendarHeader-labelContainer': {
    fontFamily: 'helvetica-neue-lt-pro, sans-serif',
    fontWeight: 400,
    color: primaryColors.white
  },
  '& .MuiPickersDay-root': {
    fontFamily: 'helvetica-neue-lt-pro, sans-serif',
    fontWeight: 400,
    color: primaryColors.white
  },
  '& .MuiDayCalendar-weekDayLabel': {
    fontFamily: 'helvetica-neue-lt-pro, sans-serif',
    fontWeight: 400,
    color: whiteShades.shade1
  },
  '& .MuiYearCalendar-root': {
    paddingRight: '0.5rem',
    paddingBottom: '0.5rem',
    padding: '0 0.5rem 0.5rem 0.5rem',
    justifyContent: 'flex-start',
    gap: '0.25rem',

    '&::-webkit-scrollbar': {
      width: '0.25rem'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: primaryColors.orange,
      borderRadius: '0.25rem'
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: primaryColors.white,
      borderRadius: '0.25rem'
    },
    '&:hover': {
      paddingRight: '0.25rem',
      '&::-webkit-scrollbar': {
        width: '0.5rem'
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: transparentColors.orangeShade4,
        borderRadius: '0.5rem'
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: transparentColors.transparent,
        borderRadius: '0.5rem'
      }
    }
  },
  '& .MuiPickersDay-root:not(.Mui-selected)': {
    border: 'none',
    backgroundColor: transparentColors.transparent,
    '&:hover': {
      border: `2px solid ${transparentColors.whiteShade2} !important`
    },
    '&:active': {
      border: `2px solid ${transparentColors.whiteShade1} !important`
    }
  },
  '.MuiPickersDay-today': {
    border: `2px solid ${transparentColors.orangeShade3} !important`
  },
  '& .MuiPickersDay-root.Mui-disabled': {
    color: `${whiteShades.shade2} !important`
  },
  '& .MuiPickersDay-root.Mui-selected': {
    backgroundColor: `${orangeShades.shade1} !important`,
    '&:hover': {
      backgroundColor: `${orangeShades.shade2} !important`
    }
  },
  /*
    The band behind the days between the two endpoints. The app mounts no MUI
    theme, so this falls back to the default palette and comes out as
    `alpha(primary.main, 0.12)`, a blue wash. It belongs here rather than in
    `datePickerStyles`: that object is the field's `sx`, and the calendar is
    portaled into the popper, so a rule placed there never matches the day.

    The filler guard is MUI's own: a week row that runs past the end of the
    month keeps rendering cells, blank ones, and they carry the highlight class
    whenever their date falls inside the range. MUI gates its background on
    `isDayFillerCell: false` as well, so without this the band bleeds into the
    empty cells and reads as a detached block after the last day of the month.
  */
  '& .MuiDateRangePickerDay-rangeIntervalDayHighlight:not(.MuiDateRangePickerDay-hiddenDayFiller)':
    {
      backgroundColor: transparentColors.whiteShade1
    },
  '& .MuiYearCalendar-button': {
    color: primaryColors.white,
    fontFamily: 'helvetica-neue-lt-pro, sans-serif',
    fontWeight: 400,
    display: 'flex',
    alignItems: 'center',
    padding: '0 0.5rem',
    margin: '0.125rem',
    width: 'fit-content',
    height: 'fit-content',
    outline: 'none !important',
    '&:hover': {
      outline: `2px solid ${transparentColors.whiteShade2} !important`
    },
    '&:active': {
      outline: `2px solid ${transparentColors.whiteShade1} !important`
    },
    '&.Mui-selected': {
      backgroundColor: `${orangeShades.shade1} !important`,
      '&:hover': {
        backgroundColor: `${orangeShades.shade2} !important`
      }
    }
  },
  '& .MuiPickersArrowSwitcher-button, .MuiPickersCalendarHeader-switchViewButton':
    {
      color: primaryColors.white,
      outline: 'none !important',
      '&:hover': {
        color: whiteShades.shade1
      },
      '&:active': {
        color: whiteShades.shade2
      }
    },
  '& .MuiPickersArrowSwitcher-button.Mui-disabled': {
    color: `${whiteShades.shade2} !important`
  }
}
